import React from 'react';
import { BaseTool, IToolManifest, IToolContext } from '../interfaces/ITool';
import { ToolComponentFactory } from '../components/ToolComponents';
import { MousePointer, CheckSquare, Square } from 'lucide-react';
import { SessionMetrics } from '../../../domains/analytics/services/SessionMetrics';

export type SelectionMode = 'single' | 'multiple' | 'all' | 'none' | 'invert' | 'connected';

// Base selection tool
export abstract class SelectionTool extends BaseTool {
  getBaseManifest(): Partial<IToolManifest> {
    return {
      name: 'Selection',
      icon: '⬚',
      category: 'edit',
      description: 'Advanced selection modes',
      version: '1.0.0',
      
      requiresSelection: false,
      
      ui: {
        toolbar: true,
        contextMenu: false,
        propertyPanel: false
      },
      
      capabilities: {
        modifiesShapes: false,
        createsShapes: false,
        deletesShapes: false,
        modifiesConnectors: false,
        createsConnectors: false,
        deletesConnectors: false,
        requiresUserInput: false,
        supportsMultiSelect: true,
        supportsBatchOperations: false
      },
      
      shortcuts: [
        {
          key: 'i',
          modifiers: ['ctrl', 'shift'],
          description: 'Invert selection'
        }
      ]
    };
  }
  
  canExecute(_context: IToolContext): boolean {
    return true;
  }
  
  // Abstract methods
  abstract getAllItemIds(): string[];
  abstract setSelection(ids: Set<string>): void;
  abstract getConnectedItems(itemId: string): string[];
  abstract getCurrentSelection(): Set<string>;
  abstract getTotalItemCount(): number;
  
  execute(params: { mode: SelectionMode; itemId?: string }): null {
    switch (params.mode) {
      case 'all':
        this.selectAll();
        break;
      case 'none':
        this.selectNone();
        break;
      case 'invert':
        this.invertSelection();
        break;
      case 'connected':
        if (params.itemId) {
          this.selectConnected(params.itemId);
        }
        break;
    }
    
    // Track selection tool usage (sampled - high frequency)
    SessionMetrics.sampled('selection_tool', { mode: params.mode });
    
    return null; // Selection operations don't create commands
  }
  
  protected selectAll(): void {
    const allIds = new Set(this.getAllItemIds());
    this.setSelection(allIds);
  }
  
  protected selectNone(): void {
    this.setSelection(new Set());
  }
  
  protected invertSelection(): void {
    const allIds = this.getAllItemIds();
    const currentSelection = this.getCurrentSelection();
    
    const newSelection = new Set<string>();
    allIds.forEach(id => {
      if (!currentSelection.has(id)) {
        newSelection.add(id);
      }
    });
    
    this.setSelection(newSelection);
  }
  
  protected selectConnected(itemId: string): void {
    const connected = new Set(this.getConnectedItems(itemId));
    connected.add(itemId);
    this.setSelection(connected);
  }
  
  getToolbarComponent(): React.ComponentType<any> {
    return ({ onExecute }: any) => {
      const selectedCount = this.getCurrentSelection().size;
      const totalCount = this.getTotalItemCount();
      
      const handleSelectionMode = (mode: string) => {
        onExecute({ mode });
      };
      
      return ToolComponentFactory.createSelect({
        icon: MousePointer,
        placeholder: `Select (${selectedCount}/${totalCount})`,
        onValueChange: handleSelectionMode,
        options: [
          {
            value: 'all',
            label: 'Select All (Ctrl+A)',
            icon: CheckSquare,
            description: undefined
          },
          {
            value: 'none',
            label: 'Deselect All (Ctrl+D)',
            icon: Square,
            description: undefined
          },
          {
            value: 'invert',
            label: 'Invert Selection',
            icon: MousePointer,
            description: undefined
          }
        ]
      });
    };
  }
}